/**
 * CustomDica.tsx — a explicação que aparece ao passar o mouse.
 *
 * Pedido dele, 05/09/2026, olhando a tela da etapa:
 *
 *   "Não tem um único tooltip nessa merda. Você já deveria ter feito tooltip em
 *   tudo, porque é uma fase complicada. As pessoas têm que entender o que está
 *   acontecendo. Cadê um tooltip 'responsável'? Quem? O que é responsável?
 *   Deixa a pessoa passar o mouse ali e entender o que é."
 *
 * ─── POR QUE UM COMPONENTE, E NÃO `title=""` ─────────────────────────────────
 * O `title` do HTML demora ~1 s para aparecer, some sozinho, não se estiliza e
 * **não existe no celular** — onde não há mouse para passar. O `OverlayTrigger`
 * do react-bootstrap (que já é a base do app: `Tabs`, `Dropdown`, `Modal`)
 * responde ao clique e ao foco também, então serve no telefone e no teclado.
 *
 * ─── DOIS FORMATOS, E O SEGUNDO NÃO OCUPA ESPAÇO ─────────────────────────────
 *   <CustomDica texto="...">                   o (i) ao lado de um rótulo
 *   <CustomDica texto="..."><span/></CustomDica>   embrulha o que já existe
 *
 * O primeiro é para o rótulo de um campo — "Responsável", "Situação". O segundo
 * é para um selo ou um número que já está na tela e cuja explicação não cabe
 * ali: o elemento continua igual, e ganha a dica.
 *
 * ⚠️ Dica NÃO é lugar de informação necessária. O que a pessoa precisa saber
 * para agir fica visível; a dica explica o que ela já vê. Regra da lei visual —
 * quem esconde o essencial atrás de um mouse esconde para quem usa o celular.
 */
import type { ReactElement, ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

let seq = 0;

interface Props {
  /** O texto da dica. Uma ou duas frases — dica longa ninguém lê. */
  texto: ReactNode;
  /** Onde ela nasce em relação ao alvo. */
  lado?: 'top' | 'bottom' | 'left' | 'right';
  /** O que recebe a dica. Sem isto, desenha o (i). */
  children?: ReactElement;
}

export function CustomDica({ texto, lado = 'top', children }: Props) {
  seq += 1;
  const id = `dica-${seq}`;
  const dica = <Tooltip id={id}>{texto}</Tooltip>;

  if (children) {
    return (
      <OverlayTrigger placement={lado} overlay={dica} trigger={['hover', 'focus']}>
        {children}
      </OverlayTrigger>
    );
  }

  return (
    <OverlayTrigger placement={lado} overlay={dica} trigger={['hover', 'focus', 'click']}>
      {/* `button`, e não um `<i>` solto: assim chega pelo Tab e responde ao
          toque no celular. `tabIndex` implícito, e o rótulo para quem lê tela. */}
      <button type="button" className="casaa-dica" aria-label="O que é isto?">
        <i className="bi bi-question-circle"></i>
      </button>
    </OverlayTrigger>
  );
}
